import { makeStyles, useTheme } from '@material-ui/styles'
import clsx from 'clsx'
import React, { FC } from 'react'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import { ensureFunction } from 'utils/fp'

type LabelRenderProp = (value: string) => string

interface Props {
  percent: number | string
  animated?: boolean
  width?: string | number
  height?: string | number
  containedPercent?: boolean
  label?: string | JSX.Element | LabelRenderProp
  variant?: 'progress' | 'health'
  color?: 'error' | 'success' | 'primary'
  showPercent?: boolean
  className?: string
}

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
  },
  label: {
    whiteSpace: 'nowrap',
    height: '100%',
    width: 30,
    paddingLeft: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  progressContainer: {
    flexGrow: 1,
    height: '100%',
    minHeight: '100%',
    backgroundColor: theme.components.table.border,
    borderRadius: 3,
  },
  '@keyframes stripes': {
    from: {
      backgroundPosition: '40px 0',
    },
    to: {
      backgroundPosition: '0 0',
    },
  },
  progress: {
    display: 'flex',
    flexFlow: 'row nowrap',
    fontSize: '12px',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    textOverflow: 'visible',
    height: '100%',
    borderRadius: theme.spacing(1),
    backgroundSize: '40px 40px',
    animation: '$stripes 2s linear infinite',
    color: '#FFF',
  },
  animated: {
    backgroundImage:
      'linear-gradient(45deg, rgba(255, 255, 255, 0.15) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.15) 75%, transparent 75%, transparent)',
  },
}))

const getBarColor = ({ theme, animated, percent, variant, color }) => {
  if (color) {
    return theme.palette[color].main
  }
  if (animated) return theme.components.graph.primary
  if (variant === 'health') {
    if (Number(percent) >= 90) return theme.components.graph.error
    if (Number(percent) >= 75) return theme.components.graph.warning
  }
  return theme.components.graph.success
}

const ProgressBar: FC<Props> = ({
  percent,
  animated = false,
  containedPercent = false,
  width = 145,
  height = 12,
  label = (progress) => `${progress}%`,
  variant = 'progress',
  color = undefined,
  showPercent = true,
  className,
}) => {
  const theme: Theme = useTheme()
  const classes = useStyles()

  const barColor = getBarColor({ theme, animated, percent, variant, color })

  return (
    <div
      className={classes.root}
      style={{
        height,
        width,
      }}
    >
      <div className={clsx(classes.progressContainer, 'progress-container')}>
        <div
          className={clsx(classes.progress, className, {
            [classes.animated]: animated,
          })}
          style={{
            width: `${percent}%`,
            backgroundColor: barColor,
          }}
        >
          {showPercent && (
            <Text variant="body2">{containedPercent ? ensureFunction(label)(percent) : null}</Text>
          )}
        </div>
      </div>
      {showPercent && !containedPercent && (
        <div className={classes.label}>
          <Text variant="body2">{ensureFunction(label)(percent)}</Text>
        </div>
      )}
    </div>
  )
}

export default ProgressBar
